import {
    ShaderMaterial,
    Uniform,
    NoBlending,
    Data3DTexture,
    FloatType,
    HalfFloatType,
    LinearFilter,
    NearestFilter,
    Vector3,
    LinearSRGBColorSpace
} from "three";

import { LookupTexture } from "postprocessing";

import fragmentShaderSource from "./glsl/LUT3D.frag";
import vertexShader from "./glsl/Common.vert";
import blendFunctions from "./glsl/BlendFunctions.frag";
import blendMode from "./glsl/BlendMode.frag";

/**
 * A simple copy shader material.
 */

export class LUT3DMaterial extends ShaderMaterial {

    constructor(lut, {
        tetrahedralInterpolation = 0,
        inputEncoding = LinearSRGBColorSpace
    } = {}) {

        let fragmentShader = fragmentShaderSource.replace("BLEND_FUNCTIONS;", blendFunctions);
        fragmentShader = fragmentShader.replace("BLEND_MODE;", blendMode);

        const defines = {};
        if (lut instanceof Data3DTexture) {
            defines.LUT_3D = "1";
        } else {
            tetrahedralInterpolation = 0;
        }

        const uniforms = {
            inputBuffer: new Uniform(null),
            opacity: new Uniform(1.0),
            blendID: new Uniform(0),
            lut: new Uniform(null),
            scale: new Uniform(new Vector3()),
            offset: new Uniform(new Vector3()),
            domainMin: new Uniform(null),
            domainMax: new Uniform(null),
            TETRAHEDRAL_INTERPOLATION: new Uniform(tetrahedralInterpolation),
            LUT_SIZE: new Uniform(1.0),
            LUT_TEXEL_WIDTH: new Uniform(1.0),
            LUT_TEXEL_HEIGHT: new Uniform(1.0),
            LUT_PRECISION_HIGH: new Uniform(1),
            LUT_STRIP_HORIZONTAL: new Uniform(0),
            CUSTOM_INPUT_DOMAIN: new Uniform(0)
        }

        super({
            name: "LUT3DMaterial",
            uniforms,
            defines,
            blending: NoBlending,
            depthWrite: false,
            depthTest: false,
            fragmentShader,
            vertexShader
        });

        this.tetrahedralInterpolation = tetrahedralInterpolation;
        this.inputColorSpace = inputEncoding;
        this.lut = lut;

    }


    /**
     * The input buffer.
     *
     * @type {Texture}
     */

    set inputBuffer(value) {

        this.uniforms.inputBuffer.value = value;

    }

    /**
     * Sets the input buffer.
     *
     * @deprecated Use inputBuffer instead.
     * @param {Number} value - The buffer.
     */

    setInputBuffer(value) {

        this.uniforms.inputBuffer.value = value;

    }

    /**
     * Returns the opacity.
     *
     * @deprecated Use opacity instead.
     * @return {Number} The opacity.
     */

    getOpacity(value) {

        return this.uniforms.opacity.value;

    }

    /**
     * Sets the opacity.
     *
     * @deprecated Use opacity instead.
     * @param {Number} value - The opacity.
     */

    setOpacity(value) {

        this.uniforms.opacity.value = value;

    }


    /**
     * The input encoding. Default is `sRGBEncoding`.
     *
     * Set this to `LinearSRGBColorSpace` if your LUT expects linear color input.
     *
     * @deprecated Use inputColorSpace instead.
     * @type {TextureEncoding}
     */

    get inputEncoding() {

        return this.inputColorSpace;

    }

    set inputEncoding(value) {

        this.inputColorSpace = value;

    }

    /**
     * Returns the input encoding.
     *
     * @deprecated Use inputColorSpace instead.
     * @return {TextureEncoding} The encoding.
     */

    getInputEncoding() {

        return this.inputColorSpace;

    }

    /**
     * Sets the input encoding.
     *
     * @deprecated Use inputColorSpace instead.
     * @param {TextureEncoding} value - The encoding.
     */

    setInputEncoding(value) {

        this.inputColorSpace = value;

    }

    /**
     * The LUT.
     *
     * @type {Texture}
     */

    get lut() {

        return this.uniforms.lut.value;

    }

    set lut(value) {

        const uniforms = this.uniforms;

        if (this.lut !== value) {

            uniforms.lut.value = value;

            if (value !== null) {

                const image = value.image;

                // Remember settings that are backed by defines.
                const tetrahedralInterpolation = this.tetrahedralInterpolation;

                uniforms.LUT_SIZE.value = Math.min(image.width, image.height).toFixed(16);
                uniforms.LUT_TEXEL_WIDTH.value = (1.0 / image.width).toFixed(16);
                uniforms.LUT_TEXEL_HEIGHT.value = (1.0 / image.height).toFixed(16);
                uniforms.domainMin.value = null;
                uniforms.domainMax.value = null;

                if (value.type === FloatType || value.type === HalfFloatType) {
                    uniforms.LUT_PRECISION_HIGH.value = 1;
                } else {
                    uniforms.LUT_PRECISION_HIGH.value = 0;
                }

                if (image.width > image.height) {
                    uniforms.LUT_STRIP_HORIZONTAL.value = 1;
                }

                if (value instanceof LookupTexture) {
                    const min = value.domainMin;
                    const max = value.domainMax;

                    if (min.x !== 0 || min.y !== 0 || min.z !== 0 || max.x !== 1 || max.y !== 1 || max.z !== 1) {
                        uniforms.CUSTOM_INPUT_DOMAIN.value = 1;
                        uniforms.domainMin.value = min.clone();
                        uniforms.domainMax.value = max.clone();
                    }
                }

                // Refresh settings that affect the LUT.
                this.tetrahedralInterpolation = tetrahedralInterpolation;

            }

        }

    }

    /**
     * Returns the current LUT.
     *
     * @deprecated Use lut instead.
     * @return {Texture} The LUT.
     */

    getLUT() {

        return this.lut;

    }

    /**
     * Sets the LUT.
     *
     * @deprecated Use lut instead.
     * @param {Texture} value - The LUT.
     */

    setLUT(value) {

        this.lut = value;

    }

    /**
     * Updates the scale and offset for the LUT sampling coordinates.
     *
     * @private
     */

    updateScaleOffset() {

        const lut = this.lut;

        if (lut !== null) {

            const size = Math.min(lut.image.width, lut.image.height);
            const scale = this.uniforms.scale.value;
            const offset = this.uniforms.offset.value;

            if (this.tetrahedralInterpolation && lut instanceof Data3DTexture) {

                if (this.uniforms.CUSTOM_INPUT_DOMAIN.value == 1) {

                    const domainScale = lut.domainMax.clone().sub(lut.domainMin);
                    scale.setScalar(size - 1).divide(domainScale);
                    offset.copy(lut.domainMin).negate().multiply(scale);

                } else {

                    scale.setScalar(size - 1);
                    offset.setScalar(0);

                }

            } else {

                if (this.uniforms.CUSTOM_INPUT_DOMAIN.value == 1) {

                    const domainScale = lut.domainMax.clone().sub(lut.domainMin).multiplyScalar(size);
                    scale.setScalar(size - 1).divide(domainScale);
                    offset.copy(lut.domainMin).negate().multiply(scale).addScalar(1.0 / (2.0 * size));

                } else {

                    scale.setScalar((size - 1) / size);
                    offset.setScalar(1.0 / (2.0 * size));

                }

            }

        }

    }

    /**
     * Configures parameters for tetrahedral interpolation.
     *
     * @private
     */

    configureTetrahedralInterpolation() {

        const lut = this.lut;

        if (lut !== null) {

            lut.minFilter = LinearFilter;
            lut.magFilter = LinearFilter;

            if (this.tetrahedralInterpolation) {

                if (lut instanceof Data3DTexture) {

                    // Interpolate samples manually.
                    lut.minFilter = NearestFilter;
                    lut.magFilter = NearestFilter;

                } else {

                    console.warn("Tetrahedral interpolation requires a 3D texture");

                }

            }

            // TODO Added for compatibility with r138. Remove later.
            if (lut.source === undefined) {

                lut.needsUpdate = true;

            }

        }

    }

    /**
     * Indicates whether tetrahedral interpolation is enabled. Requires a 3D LUT, disabled by default.
     *
     * Tetrahedral interpolation produces highly accurate results but is slower than hardware interpolation.
     *
     * @type {Boolean}
     */

    get tetrahedralInterpolation() {

        return this.uniforms.TETRAHEDRAL_INTERPOLATION;

    }

    set tetrahedralInterpolation(value) {

        if (value) {

            this.uniforms.TETRAHEDRAL_INTERPOLATION.value = 1;

        } else {

            this.uniforms.TETRAHEDRAL_INTERPOLATION.value = 0;

        }

        this.configureTetrahedralInterpolation();
        this.updateScaleOffset();
        this.setChanged();
    }

    /**
     * Enables or disables tetrahedral interpolation.
     *
     * @deprecated Use tetrahedralInterpolation instead.
     * @param {Boolean} value - Whether tetrahedral interpolation should be enabled.
     */

    setTetrahedralInterpolationEnabled(value) {

        this.tetrahedralInterpolation = value;

    }

    setChanged() {
        this.dispatchEvent({ type: "change" });
    }

}
